/**
 * SupportProvider — global access to HelpCenter from anywhere.
 * Wrap once in App; call useSupport().openHelp() to open.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { HelpCenterModal } from "./HelpCenterModal";
import type { FAQItem } from "@/lib/support/faq";

type SupportContextValue = {
  openHelp: (category?: FAQItem["category"]) => void;
  closeHelp: () => void;
};

const SupportContext = createContext<SupportContextValue | null>(null);

export function SupportProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FAQItem["category"] | undefined>();

  const openHelp = useCallback((cat?: FAQItem["category"]) => {
    setCategory(cat);
    setOpen(true);
  }, []);

  const closeHelp = useCallback(() => setOpen(false), []);

  return (
    <SupportContext.Provider value={{ openHelp, closeHelp }}>
      {children}
      <HelpCenterModal open={open} onOpenChange={setOpen} defaultCategory={category} />
    </SupportContext.Provider>
  );
}

export function useSupport() {
  const ctx = useContext(SupportContext);
  if (!ctx) {
    // Graceful fallback if provider not mounted
    return {
      openHelp: () => console.warn("[useSupport] SupportProvider not mounted"),
      closeHelp: () => {},
    };
  }
  return ctx;
}
